import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

import { ISignAccountOpController } from '@ambire-common/interfaces/signAccountOp'
import Alert from '@common/components/Alert'
import Button from '@common/components/Button'
import Text from '@common/components/Text'
import useNavigation from '@common/hooks/useNavigation'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import { isExtension } from '@web/constants/browserapi'
import { openInternalPageInTab } from '@web/extension-services/background/webapi/tab'
import useBackgroundService from '@web/hooks/useBackgroundService'

// How long Retry stays disabled after a press, so repeated taps don't stack
// re-estimations on an RPC that is already struggling.
const RETRY_COOLDOWN_MS = 3000

/**
 * Shown when the estimation or the gas price is still missing after the
 * `slowRequest` delay (see useSign). Sign needs both, so instead of a dead end
 * the user gets a way out: retry now, or switch the network's RPC (Trello #523).
 */
const SlowEstimationAlert = ({
  signAccountOpState
}: {
  signAccountOpState: ISignAccountOpController | null
}) => {
  const { t } = useTranslation()
  const { dispatch } = useBackgroundService()
  const { navigate } = useNavigation()
  const [isRetryCoolingDown, setIsRetryCoolingDown] = useState(false)
  const cooldownRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(cooldownRef.current), [])

  // `isInitialized` means the estimation landed; what is left is the gas price,
  // which the controller polls on its own interval, so Retry would not help.
  const isWaitingForEstimation = !signAccountOpState?.isInitialized
  const chainId = signAccountOpState?.accountOp.chainId

  const handleRetry = useCallback(() => {
    if (!signAccountOpState) return
    // Restarts the estimation right away, even while the previous attempt is
    // still hanging on the RPC (RecurringTimeout.restart drops it).
    dispatch({
      type: 'CURRENT_SIGN_ACCOUNT_OP_REESTIMATE',
      params: { type: signAccountOpState.type }
    })
    setIsRetryCoolingDown(true)
    clearTimeout(cooldownRef.current)
    cooldownRef.current = setTimeout(() => setIsRetryCoolingDown(false), RETRY_COOLDOWN_MS)
  }, [dispatch, signAccountOpState])

  const handleChangeRpc = useCallback(() => {
    const route = chainId
      ? `${WEB_ROUTES.networksSettings}?chainId=${chainId.toString()}`
      : WEB_ROUTES.networksSettings
    // In the extension this runs in the request window: open the settings in a
    // tab so the pending request stays where it is.
    if (isExtension) {
      openInternalPageInTab({ route }).catch(() => {})
      return
    }
    navigate(route)
  }, [chainId, navigate])

  return (
    <View style={spacings.ptTy}>
      <Alert
        type="warning"
        size="sm"
        testID="slow-estimation-alert"
        title={
          isWaitingForEstimation
            ? t(
                "Estimating this transaction is taking an unexpectedly long time. We'll keep trying, but it is possible that there's an issue with this network or RPC - please change your RPC provider or contact ZKX support if this issue persists."
              )
            : t(
                "Fetching the network fee is taking an unexpectedly long time. We'll keep trying, but it is possible that there's an issue with this network or RPC - please change your RPC provider or contact ZKX support if this issue persists."
              )
        }
      >
        {/* The request keeps the RPC it was created with, so a new RPC only
            helps once the transaction is started again. */}
        <Text fontSize={12} appearance="warningText" style={spacings.mtMi}>
          {t(
            'A new RPC applies to new transactions: after switching, reject this one and start it again.'
          )}
        </Text>
        <View style={[flexbox.directionRow, flexbox.wrap, spacings.mtSm, { gap: 8 }]}>
          {isWaitingForEstimation && (
            <Button
              type="warning"
              size="small"
              text={isRetryCoolingDown ? t('Retrying...') : t('Retry')}
              onPress={handleRetry}
              disabled={isRetryCoolingDown || !signAccountOpState}
              hasBottomSpacing={false}
              testID="slow-estimation-retry-btn"
            />
          )}
          <Button
            type="secondary"
            size="small"
            text={t('Change RPC')}
            onPress={handleChangeRpc}
            hasBottomSpacing={false}
            testID="slow-estimation-change-rpc-btn"
          />
        </View>
      </Alert>
    </View>
  )
}

export default React.memo(SlowEstimationAlert)
